import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, trackEvent } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  Clock,
  ChevronRight,
  Lock,
  Unlock,
  CheckCircle2,
  TrendingUp,
  BarChart3,
  Flame,
  Target,
  Sparkles,
  User,
  Star,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const TRIAL_LIMIT = 6;
const TRIAL_STORAGE_KEY = "trialAttemptedPassageIds";
const TRIAL_RESULTS_KEY = "trialResults";

// Helper functions for localStorage
const getTrialAttempts = () => {
  try {
    const stored = localStorage.getItem(TRIAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addTrialAttempt = (passageId) => {
  const attempts = getTrialAttempts();
  if (!attempts.includes(passageId)) {
    attempts.push(passageId);
    localStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(attempts));
  }
  return attempts;
};

const saveTrialResult = (attemptId, result) => {
  try {
    const stored = localStorage.getItem(TRIAL_RESULTS_KEY);
    const results = stored ? JSON.parse(stored) : {};
    results[attemptId] = result;
    localStorage.setItem(TRIAL_RESULTS_KEY, JSON.stringify(results));
  } catch (e) {
    console.error("Error saving trial result:", e);
  }
};

const getTrialResult = (attemptId) => {
  try {
    const stored = localStorage.getItem(TRIAL_RESULTS_KEY);
    const results = stored ? JSON.parse(stored) : {};
    return results[attemptId] || null;
  } catch {
    return null;
  }
};

const difficultyLabels = {
  1: "Beginner",
  2: "Intermediate",
  3: "Advanced",
  4: "Editorial",
};

const difficultyColors = {
  1: "bg-emerald-100 text-emerald-700",
  2: "bg-blue-100 text-blue-700",
  3: "bg-amber-100 text-amber-700",
  4: "bg-purple-100 text-purple-700",
};

const categoryIcons = {
  economy: TrendingUp,
  science: Target,
  story: BookOpen,
  biography: User,
  editorial: BookOpen,
  gk: Star,
  history: BookOpen,
  polity: BookOpen,
  environment: BookOpen,
  social: BookOpen,
};

const TrialPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [passages, setPassages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attemptedIds, setAttemptedIds] = useState([]);

  // Track trial page view
  useEffect(() => {
    trackEvent("trial_start", "/trial", { trial_type: "guest" });
  }, []);

  // Redirect authenticated users to full practice
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/practice", { replace: true });
      return;
    }
    
    setAttemptedIds(getTrialAttempts());
    fetchTrialPassages();
  }, [isAuthenticated, navigate]);

  const fetchTrialPassages = async () => {
    try {
      const response = await axios.get(`${API}/trial/passages`);
      setPassages(response.data.passages || []);
    } catch (error) {
      console.error("Error fetching trial passages:", error);
      toast.error("Failed to load trial passages");
    } finally {
      setLoading(false);
    }
  };

  const completedCount = attemptedIds.length;
  const remainingCount = Math.max(0, TRIAL_LIMIT - completedCount);
  const progressPercent = (completedCount / TRIAL_LIMIT) * 100;
  const isTrialExhausted = completedCount >= TRIAL_LIMIT;

  // Redirect to paywall if trial is exhausted
  useEffect(() => {
    if (isTrialExhausted && !loading) {
      navigate("/trial/paywall", { replace: true });
    }
  }, [isTrialExhausted, loading, navigate]);

  const handlePassageClick = (passageId) => {
    // Check if already at limit
    if (remainingCount === 0 && !attemptedIds.includes(passageId)) {
      navigate("/trial/paywall");
      return;
    }
    navigate(`/trial/practice/${passageId}`);
  };

  const benefits = [
    { icon: BookOpen, text: "Unlimited practice library" },
    { icon: BarChart3, text: "Dashboard analytics" },
    { icon: Flame, text: "Streak tracking" },
    { icon: Target, text: "Weak area insights" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-slate-200 rounded-2xl" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-48 bg-slate-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Card */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-r from-sky-600 to-indigo-600 text-white overflow-hidden" data-testid="trial-header">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-amber-300" />
                  <Badge className="bg-white/20 text-white border-0">Free Trial</Badge>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-2">
                  Try ReadMaster Free
                </h1>
                <p className="text-sky-100 max-w-lg">
                  Practice {TRIAL_LIMIT} passages for free. Create a free account to unlock unlimited practice and track your progress.
                </p>
              </div>

              {/* Progress Indicator */}
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 min-w-[200px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-sky-100">Trial Progress</span>
                  <span className="font-bold text-lg">{completedCount}/{TRIAL_LIMIT}</span>
                </div>
                <Progress value={progressPercent} className="h-2 bg-white/20" />
                <p className="text-xs text-sky-200 mt-2">
                  {remainingCount > 0 
                    ? `${remainingCount} passage${remainingCount !== 1 ? 's' : ''} remaining`
                    : "Trial complete!"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits + CTA Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Benefits Card */}
          <Card className="lg:col-span-2 border-0 shadow-md" data-testid="benefits-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Unlock className="w-5 h-5 text-sky-600" />
                Unlock Full Access
              </CardTitle>
              <CardDescription>Create a free account to get:</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3">
                {benefits.map((benefit, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                      <benefit.icon className="w-4 h-4 text-sky-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* CTA Card */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-white" data-testid="cta-card">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Ready to commit?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Join thousands of students improving daily
                </p>
                <div className="space-y-2">
                  <Button
                    onClick={() => navigate("/auth")}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    data-testid="trial-signup-btn"
                  >
                    Create Free Account
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/auth")}
                    className="w-full text-slate-600"
                    data-testid="trial-login-btn"
                  >
                    Already have an account? Sign in
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trial Passages Grid */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-slate-900 mb-1">Trial Passages</h2>
          <p className="text-slate-600 text-sm">Select a passage to start practicing</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {passages.map((passage, idx) => {
            const isAttempted = attemptedIds.includes(passage.passage_id);
            const IconComponent = categoryIcons[passage.category] || BookOpen;
            
            return (
              <Card
                key={passage.passage_id}
                className={`cursor-pointer transition-all hover:shadow-lg border ${
                  isAttempted 
                    ? "border-emerald-200 bg-emerald-50/30" 
                    : "border-slate-200 hover:border-sky-300"
                }`}
                onClick={() => handlePassageClick(passage.passage_id)}
                data-testid={`trial-passage-${idx}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      {isAttempted && (
                        <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Done
                        </Badge>
                      )}
                      <Badge className={difficultyColors[passage.difficulty]}>
                        {difficultyLabels[passage.difficulty]}
                      </Badge>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
                    {passage.title}
                  </h3>

                  <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                    {passage.content?.substring(0, 100)}...
                  </p>

                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{passage.question_count} questions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>~{Math.max(5, passage.question_count * 2)} min</span>
                    </div>
                  </div>

                  {isAttempted && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    >
                      Reattempt
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <Card className="mt-8 border-0 shadow-md bg-slate-900 text-white" data-testid="bottom-cta">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-semibold mb-2">Want more passages?</h3>
            <p className="text-slate-400 mb-4 max-w-md mx-auto">
              Create a free account to unlock our full library of 80+ passages with detailed analytics.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => navigate("/auth")}
                className="bg-white text-slate-900 hover:bg-slate-100"
                data-testid="bottom-signup-btn"
              >
                Create Free Account
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/auth")}
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="bottom-login-btn"
              >
                Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Export helpers for use in other components
export { 
  getTrialAttempts, 
  addTrialAttempt, 
  saveTrialResult, 
  getTrialResult,
  TRIAL_LIMIT,
  TRIAL_STORAGE_KEY,
  TRIAL_RESULTS_KEY
};

export default TrialPage;
